import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import {
  $getRoot,
  BLUR_COMMAND,
  COMMAND_PRIORITY_EDITOR,
  LineBreakNode,
  ParagraphNode,
} from "lexical";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListNode, ListItemNode } from "@lexical/list";
import { LinkNode } from "@lexical/link";
import { useEffect, useMemo, useRef } from "react";
import type { MutableRefObject } from "react";
import { css } from "styled-system/css";
import { Box } from "styled-system/jsx";
import { Text } from "@ttc3k/trekker";
import { ToolbarPlugin } from "./ToolbarPlugin";

export const LEXICAL_EMPTY_HTML =
  '<p class="lexical-editor-paragraph"><br></p>';

/** True when the editor output is empty (blank or Lexical’s empty paragraph). */
export function isLexicalHtmlEmpty(html: string | undefined | null): boolean {
  if (html == null) return true;
  const trimmed = html.trim();
  if (trimmed === "") return true;
  return trimmed === LEXICAL_EMPTY_HTML;
}

interface TextEditorProps {
  value?: string;
  onChange?: (content: string) => void;
  placeholder?: string;
  error?: string;
}

function normalizeStoredHtml(html: string): string {
  return html === LEXICAL_EMPTY_HTML ? "" : html;
}

// Plugin to handle HTML import (external `value` only — not after local edits)
function ImportHtmlPlugin({
  htmlString,
  skipNextImportRef,
  lastEmittedNormalizedRef,
}: {
  htmlString: string;
  skipNextImportRef: MutableRefObject<boolean>;
  lastEmittedNormalizedRef: MutableRefObject<string | null>;
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const incomingNorm = normalizeStoredHtml(htmlString);

    // Skip only when this `value` update is from our own debounced export — not when
    // the parent replaced `value` with async-loaded or different external HTML.
    if (skipNextImportRef.current) {
      if (incomingNorm === lastEmittedNormalizedRef.current) {
        skipNextImportRef.current = false;
        return;
      }
      skipNextImportRef.current = false;
    }

    editor.update(
      () => {
        const root = $getRoot();
        const currentHtml = $generateHtmlFromNodes(editor, null);

        if (htmlString && htmlString !== "") {
          if (currentHtml !== htmlString) {
            const parser = new DOMParser();
            const dom = parser.parseFromString(htmlString, "text/html");
            const nodes = $generateNodesFromDOM(editor, dom);
            root.clear();
            root.append(...nodes);
          }
        } else {
          const isEmpty =
            currentHtml === LEXICAL_EMPTY_HTML || currentHtml === "";
          if (!isEmpty) {
            root.clear();
          }
        }
      },
      { tag: "html-import" },
    );

    lastEmittedNormalizedRef.current = incomingNorm;
  }, [editor, htmlString, skipNextImportRef, lastEmittedNormalizedRef]);

  return null;
}

const EXPORT_DEBOUNCE_MS = 200;

/**
 * Serializes editor → HTML on a debounced schedule so we do not run
 * `$generateHtmlFromNodes` (expensive) nor parent setState on every keystroke.
 * Flushes on blur. We intentionally do **not** flush on unmount: Lexical is often
 * mid-teardown then, so `$generateHtmlFromNodes` can be empty and would call
 * `onChange("")`, wiping a controlled field that still has saved HTML (e.g. remount
 * after `key` change or closing a flyup).
 */
function ExportHtmlPlugin({
  onHtmlChangeRef,
  valueRef,
  skipNextImportRef,
  lastEmittedNormalizedRef,
}: {
  onHtmlChangeRef: MutableRefObject<(html: string) => void>;
  valueRef: MutableRefObject<string>;
  skipNextImportRef: MutableRefObject<boolean>;
  lastEmittedNormalizedRef: MutableRefObject<string | null>;
}) {
  const [editor] = useLexicalComposerContext();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const flush = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      editor.getEditorState().read(() => {
        const rawHtml = $generateHtmlFromNodes(editor, null);
        const normalized = normalizeStoredHtml(rawHtml);
        if (normalized === normalizeStoredHtml(valueRef.current)) {
          return;
        }
        skipNextImportRef.current = true;
        lastEmittedNormalizedRef.current = normalized;
        onHtmlChangeRef.current(rawHtml);
      });
    };

    const scheduleFlush = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        flush();
      }, EXPORT_DEBOUNCE_MS);
    };

    const removeUpdate = editor.registerUpdateListener(({ tags }) => {
      if (tags.has("html-import")) {
        return;
      }
      scheduleFlush();
    });

    const removeBlur = editor.registerCommand(
      BLUR_COMMAND,
      () => {
        flush();
        return false;
      },
      COMMAND_PRIORITY_EDITOR,
    );

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      removeUpdate();
      removeBlur();
    };
  }, [editor, onHtmlChangeRef, skipNextImportRef, valueRef, lastEmittedNormalizedRef]);

  return null;
}

export const TextEditor = ({
  value = "",
  onChange,
  placeholder = "",
  error,
}: TextEditorProps) => {
  const skipNextImportRef = useRef(false);
  const lastEmittedNormalizedRef = useRef<string | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  const onHtmlChangeRef = useRef<(html: string) => void>(() => {});
  onHtmlChangeRef.current = (html: string) => {
    const val = normalizeStoredHtml(html);
    onChange?.(val);
  };

  const contentEditableClassName = useMemo(
    () =>
      css({
        color: "text.dark",
        fontFamily: "body",
        fontSize: "md",
        lineHeight: "sm",
        px: "200",
        pt: "84px",
        pb: "200",
        borderRadius: "150",
        border: "1px solid",
        borderColor: "border.light",
        outline: "1px solid",
        outlineColor: "transparent",
        bg: "white",
        minH: "320px",
        textAlign: "left",
        transitionProperty: "outline-color, border-color",
        transitionDuration: "normal",
        transitionTimingFunction: "default",
        _hover: {
          outlineColor: "border.mid",
          borderColor: "border.mid",
        },
        _focusVisible: {
          outlineColor: "border.dark",
          borderColor: "border.dark",
          _hover: {
            outlineColor: "border.dark",
            borderColor: "border.dark",
          },
        },
        ...(error
          ? {
              color: "text.error.dark",
              borderColor: "border.error",
              boxShadow: "0 0 0 1px token(colors.border.error)",
            }
          : {}),
        "& a": {
          color: "text.accent.blue.dark",
          cursor: "pointer",
        },
        "& ul": {
          listStyleType: "none",
          pl: "0",
        },
        "& ul li": {
          position: "relative",
          pl: "1.2em",
          _before: {
            content: "'•'",
            position: "absolute",
            left: "0",
            color: "text.dark",
          },
        },
        "& blockquote": {
          borderLeft: "2px solid",
          borderLeftColor: "border.light",
          pl: "400",
          ml: "0",
          mr: "0",
          my: "300",
          fontStyle: "italic",
          py: "250",
          borderRadius: "0 150 150 0",
        },
        "& em, .lexical-editor-text-italic": {
          fontStyle: "italic",
        },
        "& u, .lexical-editor-text-underline": {
          textDecoration: "underline",
        },
        "& strong, .lexical-editor-text-bold": {
          fontWeight: "semiBold",
        },
        "& .lexical-editor-root": {
          textAlign: "left",
        },
        "& .lexical-editor-paragraph": {
          textAlign: "left",
        },
      }),
    [error],
  );

  const initialConfig = {
    namespace: "TextEditor",
    theme: {
      // Theme styles will be applied via CSS classes
      root: "lexical-editor-root",
      paragraph: "lexical-editor-paragraph",
      text: {
        bold: "lexical-editor-text-bold",
        italic: "lexical-editor-text-italic",
        underline: "lexical-editor-text-underline",
      },
      link: "lexical-editor-link",
      list: {
        nested: {
          listitem: "lexical-editor-nested-listitem",
        },
        ol: "lexical-editor-list-ol",
        ul: "lexical-editor-list-ul",
        listitem: "lexical-editor-listitem",
      },
      quote: "lexical-editor-quote",
    },
    nodes: [
      ParagraphNode,
      LineBreakNode,
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      LinkNode,
    ],
    onError: (error: unknown) => {
      console.error("Lexical editor error:", error);
    },
  };

  return (
    <Box position={"relative"}>
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarPlugin />
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className={contentEditableClassName}
              placeholder={
                <div
                  className={css({
                    color: "text.light",
                    pointerEvents: "none",
                    userSelect: "none",
                    position: "absolute",
                    top: "84px",
                    left: "200",
                  })}
                >
                  {placeholder}
                </div>
              }
              aria-placeholder={placeholder}
            />
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <LinkPlugin />
        <ListPlugin />
        <ImportHtmlPlugin
          htmlString={value}
          skipNextImportRef={skipNextImportRef}
          lastEmittedNormalizedRef={lastEmittedNormalizedRef}
        />
        <ExportHtmlPlugin
          onHtmlChangeRef={onHtmlChangeRef}
          valueRef={valueRef}
          skipNextImportRef={skipNextImportRef}
          lastEmittedNormalizedRef={lastEmittedNormalizedRef}
        />
      </LexicalComposer>
      {error && (
        <Text visual={"smallMedium"} color={"text.error.dark"} mt={"50"}>
          {error}
        </Text>
      )}
    </Box>
  );
};
